import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Task } from "@/contexts/TaskContext";
import { mapDbTaskToTask } from "@/lib/taskMapper";
import { TASK_SELECT_COLUMNS } from "@/lib/taskSelectColumns";
import { cacheConfig } from "@/constants/queryKeys";
import { useEffect, useMemo } from "react";
import { warmBlurCache } from "@/components/TaskImage";

// Page size for server-side pagination
// PERF: Zwiększono z 1 → 10 zadań w pierwszej paczce.
// Przy 1 zadaniu user rozwiązywał je szybciej niż leciała paczka tła (~300 ms opóźnienia)
// i widział spinner między zadaniem 1 a 2. Z paczką 10 zadań ma zapas do dalszej nawigacji
// zanim tło doładuje resztę. Payload wzrasta z ~5 kB do ~50 kB — dalej mieści się w jednym round-tripie.
export const INITIAL_TASKS_PAGE_SIZE = 10;
export const BACKGROUND_TASKS_PAGE_SIZE = 500; // reszta w jednym request
export const TASKS_PAGE_SIZE = INITIAL_TASKS_PAGE_SIZE; // kompatybilność

// Query keys for paginated tasks
export const paginatedTaskKeys = {
  all: ["tasks", "paginated"] as const,
  list: (filters: Record<string, unknown>) => [...paginatedTaskKeys.all, filters] as const,
};

interface FetchTasksPageParams {
  pageParam?: number;
  filters?: {
    categories?: string[];
    years?: number[];
    docTypes?: string[];
    formula?: string | null;
    operationalVerbs?: string[];
    taskSubtypes?: string[];
  };
}

// Fetch a page of tasks with optional filters
const fetchTasksPage = async ({ pageParam = 0, filters }: FetchTasksPageParams) => {
  // First page: load only INITIAL_TASKS_PAGE_SIZE for instant start
  // Subsequent pages: load BACKGROUND_TASKS_PAGE_SIZE (1 at a time)
  const isFirstPage = pageParam === 0;
  const pageSize = isFirstPage ? INITIAL_TASKS_PAGE_SIZE : BACKGROUND_TASKS_PAGE_SIZE;
  
  let query = supabase
    .from("tasks")
    .select(
      TASK_SELECT_COLUMNS,
      { count: "exact" }
    )
    .order("created_at", { ascending: false })
    .range(pageParam, pageParam + pageSize - 1);

  // Apply filters
  if (filters?.categories && filters.categories.length > 0) {
    query = query.overlaps("categories", filters.categories);
  }
  if (filters?.years && filters.years.length > 0) {
    query = query.in("year", filters.years);
  }
  if (filters?.docTypes && filters.docTypes.length > 0) {
    query = query.in("document_type", filters.docTypes);
  }
  if (filters?.formula) {
    query = query.eq("formula", filters.formula);
  }
  if (filters?.taskSubtypes && filters.taskSubtypes.length > 0) {
    query = query.in("task_subtype", filters.taskSubtypes);
  }
  if (filters?.operationalVerbs && filters.operationalVerbs.length > 0) {
    const verbFilters = filters.operationalVerbs.map(v => `operational_verb.ilike.%${v}%`).join(",");
    query = query.or(verbFilters);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  const tasks = (data || []).map(d => mapDbTaskToTask(d));
  const nextOffset = pageParam + pageSize;
  const hasMore = count !== null && nextOffset < count;

  return {
    tasks,
    nextOffset: hasMore ? nextOffset : undefined,
    totalCount: count || 0,
  };
};

interface UsePaginatedTasksQueryOptions {
  filters?: {
    categories?: string[];
    years?: number[];
    docTypes?: string[];
    formula?: string | null;
    operationalVerbs?: string[];
    taskSubtypes?: string[];
  };
  enabled?: boolean;
}

// Hook for paginated tasks with infinite scroll
export function usePaginatedTasksQuery(options: UsePaginatedTasksQueryOptions = {}) {
  const { filters, enabled = true } = options;

  return useInfiniteQuery({
    queryKey: paginatedTaskKeys.list(filters || {}),
    queryFn: ({ pageParam }) => fetchTasksPage({ pageParam, filters }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    staleTime: cacheConfig.tasks.staleTime,
    gcTime: cacheConfig.tasks.gcTime,
    enabled,
  });
}

// Helper hook to get flattened task list from infinite query
export function useFlattenedTasks(options: UsePaginatedTasksQueryOptions = {}) {
  const query = usePaginatedTasksQuery(options);
  
  const tasks = useMemo(
    () => query.data?.pages.flatMap((page) => page.tasks) || [],
    [query.data?.pages]
  );
  const totalCount = query.data?.pages[0]?.totalCount || 0;

  // Proactively warm blur cache for the loaded page(s) so first render of
  // any task shows a blur placeholder instead of a neutral background.
  useEffect(() => {
    if (tasks.length === 0) return;
    warmBlurCache(tasks);
  }, [tasks.length]);

  return {
    ...query,
    tasks,
    totalCount,
    loadedCount: tasks.length,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
}

// Hook to prefetch next page
export function usePrefetchNextTasksPage() {
  const queryClient = useQueryClient();

  return async (currentOffset: number, filters?: UsePaginatedTasksQueryOptions["filters"]) => {
    const nextOffset = currentOffset + TASKS_PAGE_SIZE;
    
    await queryClient.prefetchInfiniteQuery({
      queryKey: paginatedTaskKeys.list(filters || {}),
      queryFn: ({ pageParam }) => fetchTasksPage({ pageParam, filters }),
      initialPageParam: nextOffset,
    });
  };
}
